import { useEffect, useMemo, useRef } from 'react';
import {
  ComboboxField,
  type ComboboxFieldOption,
} from 'components/DesignSystems/ComboboxField/ComboboxField';
import { useAllIncidents } from 'components/IAP/hooks/incidentHooks';
import { useGroupGuid } from 'components/IAP/hooks/commonHooks';
import { useListLayerNodes } from 'hooks/layerNode/useListLayerNodes';
import type { LayerGroupDto, LayerNodeDto } from 'hooks/layerNode/types';

type IncidentSelectProps = {
  value: string | null;
  onChange: (incidentId: string | null) => void;
  selectedFolderId?: string | null;
  label?: string;
  isDisabled?: boolean;
};

export function IncidentSelect({
  value,
  onChange,
  selectedFolderId,
  label = 'Incident',
  isDisabled,
}: IncidentSelectProps) {
  const { data: incidents = [] } = useAllIncidents();
  const groupGuid = useGroupGuid();
  const { data: layerNodeGroups } = useListLayerNodes(
    { filter: { groupGuid } },
    { enabled: Boolean(groupGuid) }
  );

  const options: ComboboxFieldOption[] = useMemo(
    () =>
      incidents.map((incident) => ({
        value: incident.id,
        label: incident.name,
      })),
    [incidents]
  );

  // Incident that the selected category (folder) belongs to, if any.
  const folderIncidentId = useMemo(() => {
    if (!selectedFolderId) return undefined;

    return (layerNodeGroups?.data ?? [])
      .filter(
        (group): group is LayerGroupDto & { folder: LayerNodeDto } =>
          group.folder != null
      )
      .map((group) => group.folder)
      .find((folder) => folder.id === selectedFolderId)?.incidentId;
  }, [layerNodeGroups?.data, selectedFolderId]);

  const valueRef = useRef(value);
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    valueRef.current = value;
    onChangeRef.current = onChange;
  });

  // Reset or select the incident based on the selected category (folder).
  useEffect(() => {
    if (!folderIncidentId || isDisabled) return;
    if (valueRef.current !== folderIncidentId) {
      onChangeRef.current(folderIncidentId);
    }
  }, [folderIncidentId, isDisabled]);

  return (
    <div data-testid="incident-select">
      <ComboboxField
        label={label}
        options={options}
        disabled={isDisabled}
        value={value}
        onChange={(next) => onChange(Array.isArray(next) ? null : next)}
      />
    </div>
  );
}
